import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  LogOut, User, CreditCard, Shield, FileText,
  CheckCircle, X, Loader, Sparkles, Zap, Crown,
  Image, Video, ArrowUpRight, Calendar, Receipt,
  AlertTriangle, ChevronRight, ExternalLink, Rocket,
  Settings
} from 'lucide-react';
import { authService } from '../../services/PlatformService';
import { stripeService } from '../../services/StripeService';
import { OnboardingService } from '../../services/OnboardingService';

const UserAccount = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [userData, setUserData] = useState(null);
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [onboardingStatus, setOnboardingStatus] = useState(null);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [isLoadingPortal, setIsLoadingPortal] = useState(false);
  const [isLoadingUpgrade, setIsLoadingUpgrade] = useState(false);

  useEffect(() => {
    // Check for success parameters in URL
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('session_id')) {
      setSuccessMessage('Assinatura atualizada com sucesso! Suas mudanças serão refletidas em breve.');
      window.history.replaceState({}, document.title, window.location.pathname);
    }
    if (urlParams.get('subscription_success')) {
      setSuccessMessage('🎉 Bem-vindo! Sua assinatura foi ativada com sucesso!');
      window.history.replaceState({}, document.title, window.location.pathname);
    }

    loadData();
  }, []);

  const loadData = async () => {
    setIsLoading(true);
    setError(null);

    try {
      // Carregar dados do usuário
      const userResponse = await authService.getUser();
      setUserData(userResponse.data);

      // Carregar status da assinatura
      try {
        const subResponse = await stripeService.getSubscriptionStatus();
        if (subResponse.success) {
          setSubscriptionData(subResponse.data);
        }
      } catch (subError) {
        console.warn('Erro ao carregar assinatura:', subError);
        // Não é erro crítico, usuário pode não ter assinatura
      }

      // Carregar status do onboarding
      try {
        const onboardingResponse = await OnboardingService.getStatus();
        setOnboardingStatus(onboardingResponse);
      } catch (onboardingError) {
        console.warn('Erro ao carregar status do onboarding:', onboardingError);
        // Não é erro crítico
      }
    } catch (err) {
      console.error('Erro ao carregar dados:', err);
      setError('Falha ao carregar seus dados. Por favor, tente novamente.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('userData');
    window.location.href = '/login';
  };

  const handleManageSubscription = async () => {
    setIsLoadingPortal(true);
    try {
      await stripeService.redirectToPortal();
    } catch (err) {
      console.error('Erro ao abrir portal:', err);
      setError('Erro ao abrir portal de gerenciamento. Tente novamente.');
    } finally {
      setIsLoadingPortal(false);
    }
  };

  // 🆕 ATUALIZADO: Função de upgrade/downgrade
  // Agora vai direto para /plans onde o usuário pode escolher
  const handleUpgrade = () => {
    navigate('/plans');
  };

  // 🆕 NOVO: Ir direto para checkout de um plano específico
  const handleDirectCheckout = async (plan) => {
    setIsLoadingUpgrade(true);
    try {
      await stripeService.redirectToCheckout(plan);
    } catch (err) {
      console.error('Erro ao iniciar checkout:', err);
      setError('Erro ao iniciar processo de assinatura. Tente novamente.');
    } finally {
      setIsLoadingUpgrade(false);
    }
  };

  const handleContinueOnboarding = () => {
    navigate('/onboarding');
  };

  // Informações dos planos
  const planInfo = {
    free: {
      name: 'Gratuito',
      icon: User,
      color: 'gray',
      bgColor: 'bg-gray-100',
      textColor: 'text-gray-700',
      borderColor: 'border-gray-300'
    },
    basic: {
      name: 'Básico',
      icon: Zap,
      color: 'blue',
      bgColor: 'bg-blue-100',
      textColor: 'text-blue-700',
      borderColor: 'border-blue-300'
    },
    pro: {
      name: 'Pro',
      icon: Sparkles,
      color: 'amber',
      bgColor: 'bg-amber-100',
      textColor: 'text-amber-700',
      borderColor: 'border-amber-300'
    },
    max: {
      name: 'Max',
      icon: Crown,
      color: 'purple',
      bgColor: 'bg-purple-100',
      textColor: 'text-purple-700',
      borderColor: 'border-purple-300'
    }
  };

  // 🆕 Obter próximo plano para upgrade rápido
  const getNextPlan = (currentPlan) => {
    const upgradeMap = {
      free: 'basic',
      basic: 'pro',
      pro: 'max'
    };
    return upgradeMap[currentPlan] || null;
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-orange-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-20 h-20 mx-auto mb-6 rounded-3xl overflow-hidden bg-gradient-to-br from-amber-100 to-orange-100 p-1 animate-pulse">
            <div className="w-full h-full rounded-3xl overflow-hidden bg-white flex items-center justify-center">
              <User className="w-10 h-10 text-amber-400" />
            </div>
          </div>
          <p className="text-xl text-gray-700 font-medium">Carregando suas informações...</p>
        </div>
      </div>
    );
  }

  const user = userData || { name: "Usuário", email: "usuario@exemplo.com" };
  const subscription = subscriptionData || { plan: 'free', status: 'inactive', usage: { images: { used: 0, limit: 5 }, videos: { used: 0, limit: 0 } } };
  
  const currentPlan = planInfo[subscription.plan] || planInfo.free;
  const PlanIcon = currentPlan.icon;
  const statusInfo = stripeService.helpers.formatStatus(subscription.status);
  const nextPlan = getNextPlan(subscription.plan);
  const nextPlanInfo = nextPlan ? planInfo[nextPlan] : null;

  // Calcular percentuais de uso
  const imageUsagePercent = stripeService.helpers.calculateUsagePercentage(
    subscription.usage?.images?.used || 0,
    subscription.usage?.images?.limit || 1
  );
  const videoUsagePercent = stripeService.helpers.calculateUsagePercentage(
    subscription.usage?.videos?.used || 0,
    subscription.usage?.videos?.limit || 1
  );

  // Verificar se onboarding está incompleto
  const isOnboardingIncomplete = onboardingStatus && !onboardingStatus.completed;

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-orange-50">
      {/* Success Message */}
      {successMessage && (
        <div className="fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-xl shadow-lg flex items-center gap-3 z-50 animate-slide-in">
          <CheckCircle className="w-5 h-5" />
          <span>{successMessage}</span>
          <button
            onClick={() => setSuccessMessage(null)}
            className="ml-2 hover:opacity-80"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="fixed top-4 right-4 bg-red-500 text-white px-6 py-3 rounded-xl shadow-lg flex items-center gap-3 z-50 animate-slide-in">
          <AlertTriangle className="w-5 h-5" />
          <span>{error}</span>
          <button
            onClick={() => setError(null)}
            className="ml-2 hover:opacity-80"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Minha Conta</h1>
          <p className="text-gray-600">Gerencie sua conta e assinatura</p>
        </div>

        {/* Alerta de Onboarding Incompleto */}
        {isOnboardingIncomplete && (
          <div className="mb-8 p-6 bg-gradient-to-r from-purple-50 to-pink-50 border-2 border-purple-200 rounded-2xl">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center">
                  <Rocket className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Complete sua configuração!</h3>
                  <p className="text-sm text-gray-600">
                    Finalize o onboarding para ter conteúdos mais personalizados
                  </p>
                </div>
              </div>
              <button
                onClick={handleContinueOnboarding}
                className="px-6 py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white font-semibold rounded-xl hover:from-purple-600 hover:to-pink-600 transition-all"
              >
                Continuar
              </button>
            </div>
          </div>
        )}

        {/* Profile Card */}
        <div className="bg-white/80 backdrop-blur-sm rounded-3xl shadow-xl overflow-hidden mb-8">
          {/* Header do Perfil */}
          <div className="p-8 bg-gradient-to-r from-amber-400 via-orange-500 to-red-500 text-white">
            <div className="flex items-center gap-6">
              <div className="w-24 h-24 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center overflow-hidden">
                {user.photo_url ? (
                  <img src={user.photo_url} alt={user.name} className="w-full h-full object-cover" />
                ) : (
                  <User className="w-12 h-12 text-white" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <h1 className="font-bold text-3xl truncate">{user.name}</h1>
                <p className="text-lg text-white/90 truncate mt-2">{user.email}</p>
              </div>
            </div>
          </div>

          {/* Logout Button */}
          <div className="p-6 border-t border-gray-200">
            <button
              onClick={handleLogout}
              className="flex items-center gap-3 px-6 py-3 rounded-xl text-red-600 hover:bg-red-50 font-medium transition-all"
            >
              <LogOut className="w-5 h-5" />
              Sair da Conta
            </button>
          </div>
        </div>

      {/* Seção do Plano */}
<div className="bg-white/80 backdrop-blur-sm rounded-3xl shadow-xl overflow-hidden mb-8">
  <div className="p-4 sm:p-6 border-b border-gray-200">
    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
      <div className="flex items-center gap-3">
        <div className={`p-3 rounded-xl ${currentPlan.bgColor} flex-shrink-0`}>
          <PlanIcon className={`w-6 h-6 ${currentPlan.textColor}`} />
        </div>
        <div>
          <h2 className="text-lg sm:text-xl font-bold text-gray-900">Meu Plano</h2>
          <div className="flex items-center gap-2 mt-1 flex-wrap">
            <span className={`px-3 py-1 rounded-full text-xs sm:text-sm font-semibold ${currentPlan.bgColor} ${currentPlan.textColor}`}>
              {currentPlan.name}
            </span>
            {subscription.billing_interval && subscription.plan !== 'free' && (
              <span className={`px-3 py-1 rounded-full text-xs sm:text-sm font-semibold ${
                subscription.billing_interval === 'yearly' 
                  ? 'bg-green-100 text-green-700' 
                  : 'bg-gray-100 text-gray-600'
              }`}>
                {subscription.billing_interval === 'yearly' ? '📅 Anual' : 'Mensal'}
              </span>
            )}
            <span className={`px-3 py-1 rounded-full text-xs sm:text-sm font-semibold ${statusInfo.bgColor} ${statusInfo.textColor}`}>
              {statusInfo.text}
            </span>
          </div>
        </div>
      </div>

      {/* Botões de Ação */}
      <div className="flex items-center gap-2 sm:gap-3 w-full sm:w-auto">
        {subscription.plan !== 'free' && subscription.status !== 'canceled' ? (
          <button
            onClick={handleManageSubscription}
            disabled={isLoadingPortal}
            className="inline-flex items-center justify-center gap-2 px-3 sm:px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-xl transition-all disabled:opacity-50 flex-1 sm:flex-initial text-sm"
          >
            {isLoadingPortal ? (
              <Loader className="w-4 h-4 animate-spin" />
            ) : (
              <CreditCard className="w-4 h-4" />
            )}
            Gerenciar
          </button>
        ) : null}
        
        {subscription.plan !== 'max' && (
          <button
            onClick={handleUpgrade}
            disabled={isLoadingUpgrade}
            className="inline-flex items-center justify-center gap-2 px-3 sm:px-4 py-2 bg-gradient-to-r from-amber-400 to-orange-500 hover:from-amber-500 hover:to-orange-600 text-white font-semibold rounded-xl transition-all disabled:opacity-50 flex-1 sm:flex-initial text-sm"
          >
            {isLoadingUpgrade ? (
              <Loader className="w-4 h-4 animate-spin" />
            ) : (
              <ArrowUpRight className="w-4 h-4" />
            )}
            {subscription.plan === 'free' ? 'Ver Planos' : 'Upgrade'}
          </button>
        )}
      </div>
    </div>
  </div>

  {/* Uso Atual */}
  <div className="p-4 sm:p-6">
    <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">
      Créditos do Mês
    </h3>

    {/* Saldo de créditos (1 crédito = 1 imagem · 5 créditos = 1 vídeo) */}
    <div className="p-4 bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-2xl mb-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-amber-600" />
          <span className="font-medium text-gray-900">Créditos disponíveis</span>
        </div>
        <span className="text-lg font-bold text-gray-900">
          {subscription.usage?.credits?.balance ?? 0}
          <span className="text-sm font-normal text-gray-500"> / {subscription.usage?.credits?.monthly ?? 0}</span>
        </span>
      </div>
      <div className="h-2 bg-white rounded-full overflow-hidden border border-amber-100">
        <div
          className="h-full bg-gradient-to-r from-amber-400 to-orange-500 transition-all duration-500"
          style={{
            width: `${Math.min(100, ((subscription.usage?.credits?.balance ?? 0) / Math.max(1, subscription.usage?.credits?.monthly ?? 1)) * 100)}%`
          }}
        />
      </div>
      <p className="text-xs text-gray-500 mt-2">
        1 crédito por imagem · 5 créditos por vídeo · renova todo mês
      </p>
    </div>

    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {/* Imagens */}
      <div className="p-4 bg-gray-50 rounded-2xl">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Image className="w-5 h-5 text-blue-600" />
            <span className="font-medium text-gray-900">Imagens</span>
          </div>
          <span className="text-sm text-gray-600">
            {subscription.usage?.images?.used || 0} / {subscription.usage?.images?.limit || 0}
          </span>
        </div>
        <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
          <div
            className={`h-full transition-all duration-500 ${stripeService.helpers.getUsageColor(imageUsagePercent)}`}
            style={{ width: `${Math.min(100, imageUsagePercent)}%` }}
          />
        </div>
        <p className="text-xs text-gray-500 mt-2">
          {subscription.usage?.images?.remaining || 0} restantes
        </p>
      </div>

      {/* Vídeos */}
      <div className="p-4 bg-gray-50 rounded-2xl">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Video className="w-5 h-5 text-purple-600" />
            <span className="font-medium text-gray-900">Vídeos</span>
          </div>
          <span className="text-sm text-gray-600">
            {subscription.usage?.videos?.used || 0} / {subscription.usage?.videos?.limit || 0}
          </span>
        </div>
        <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
          <div
            className={`h-full transition-all duration-500 ${stripeService.helpers.getUsageColor(videoUsagePercent)}`}
            style={{ width: `${Math.min(100, videoUsagePercent)}%` }}
          />
        </div>
        <p className="text-xs text-gray-500 mt-2">
          {subscription.usage?.videos?.remaining || 0} restantes
        </p>
      </div>
    </div>

    {/* Informações do Período */}
    {subscription.plan !== 'free' && (
      <div className="mt-4 pt-4 border-t border-gray-200">
        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center gap-2 text-gray-600">
            <Calendar className="w-4 h-4" />
            <span>Próxima renovação {subscription.billing_interval === 'yearly' ? '(anual)' : ''}</span>
          </div>
          <span className="font-medium text-gray-900">
            {stripeService.helpers.formatDate(subscription.current_period_end)}
          </span>
        </div>
        
        {subscription.cancel_at_period_end && (
          <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-xl">
            <p className="text-sm text-amber-800">
              ⚠️ Sua assinatura será cancelada em {stripeService.helpers.formatDate(subscription.current_period_end)}
            </p>
          </div>
        )}

        {subscription.status === 'trialing' && subscription.trial_end && (
          <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-xl">
            <p className="text-sm text-blue-800">
              🎁 Período de teste até {stripeService.helpers.formatDate(subscription.trial_end)}
            </p>
          </div>
        )}
      </div>
    )}

    {/* CTA para usuários Free */}
    {subscription.plan === 'free' && (
      <div className="mt-4 p-4 bg-gradient-to-r from-amber-50 to-orange-50 border-2 border-amber-200 rounded-2xl">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h4 className="font-semibold text-gray-900">Desbloqueie todo o potencial</h4>
            <p className="text-sm text-gray-600 mt-1">
              Assine um plano e gere até 500 imagens e 10 vídeos por mês!
            </p>
          </div>
          <button
            onClick={handleUpgrade}
            disabled={isLoadingUpgrade}
            className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-amber-400 to-orange-500 hover:from-amber-500 hover:to-orange-600 text-white font-semibold rounded-xl transition-all whitespace-nowrap disabled:opacity-50 w-full sm:w-auto"
          >
            {isLoadingUpgrade ? (
              <Loader className="w-4 h-4 animate-spin" />
            ) : (
              <>
                Ver Planos
                <ChevronRight className="w-4 h-4" />
              </>
            )}
          </button>
        </div>
      </div>
    )}

    {/* Sugestão de Upgrade para planos pagos (não max) */}
    {subscription.plan !== 'free' && subscription.plan !== 'max' && nextPlanInfo && (
      <div className="mt-4 p-4 bg-gradient-to-r from-purple-50 to-indigo-50 border-2 border-purple-200 rounded-2xl">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${nextPlanInfo.bgColor} flex-shrink-0`}>
              {nextPlanInfo.icon && <nextPlanInfo.icon className={`w-5 h-5 ${nextPlanInfo.textColor}`} />}
            </div>
            <div>
              <h4 className="font-semibold text-gray-900">
                Precisa de mais recursos?
              </h4>
              <p className="text-sm text-gray-600">
                Faça upgrade para o plano {nextPlanInfo.name} e tenha mais limites
              </p>
            </div>
          </div>
          <button
            onClick={handleUpgrade}
            className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white font-semibold rounded-xl transition-all whitespace-nowrap w-full sm:w-auto"
          >
            Ver opções
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    )}
  </div>
</div>

        {/* Seção de Configurações - Onboarding */}
        {onboardingStatus && (
          <div className="bg-white/80 backdrop-blur-sm rounded-3xl shadow-xl overflow-hidden mb-8">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center gap-3">
                <div className="p-3 rounded-xl bg-purple-100">
                  <Settings className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900">Configurações de Conteúdo</h2>
                  <p className="text-sm text-gray-500 mt-1">
                    Personalize como a IA gera seus conteúdos
                  </p>
                </div>
              </div>
            </div>
            
            <div className="p-6">
              {onboardingStatus.completed ? (
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500" />
                    <div>
                      <p className="font-medium text-gray-900">Configuração completa</p>
                      <p className="text-sm text-gray-500">
                        {onboardingStatus.completed_at 
                          ? `Finalizada em ${new Date(onboardingStatus.completed_at).toLocaleDateString('pt-BR')}`
                          : 'Suas preferências estão salvas'}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => navigate('/onboarding?redo=true')}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-xl transition-all"
                  >
                    Refazer configuração
                  </button>
                </div>
              ) : (
                <div className="p-4 bg-gradient-to-r from-purple-50 to-pink-50 border-2 border-purple-200 rounded-2xl">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-semibold text-gray-900">Finalize sua configuração</h4>
                      <p className="text-sm text-gray-600 mt-1">
                        Complete o onboarding para ter conteúdos mais personalizados
                      </p>
                      <div className="flex items-center gap-2 mt-2">
                        <div className="w-32 h-2 bg-purple-200 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-gradient-to-r from-purple-500 to-pink-500"
                            style={{ width: `${onboardingStatus.progress?.percentage || 0}%` }}
                          />
                        </div>
                        <span className="text-xs text-purple-600 font-medium">
                          Etapa {onboardingStatus.current_step || 1} de 7
                        </span>
                      </div>
                    </div>
                    <button
                      onClick={handleContinueOnboarding}
                      className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white font-semibold rounded-xl transition-all whitespace-nowrap"
                    >
                      Continuar
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Seções de Links */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Termos de Serviço */}
          <Link
            to="/terms"
            className="p-6 bg-white/80 backdrop-blur-sm rounded-2xl border-2 border-gray-200 hover:border-purple-300 hover:shadow-lg transition-all group"
          >
            <div className="flex items-start gap-4">
              <div className="p-3 rounded-xl bg-purple-100 group-hover:bg-purple-200 transition-colors">
                <FileText className="w-6 h-6 text-purple-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900 text-lg group-hover:text-purple-700 transition-colors">
                  Termos de Serviço
                </h3>
                <p className="text-sm text-gray-600 mt-1">
                  Leia nossos termos e condições de uso
                </p>
              </div>
              <ExternalLink className="w-5 h-5 text-gray-400 group-hover:text-purple-600 transition-colors" />
            </div>
          </Link>

          {/* Política de Privacidade */}
          <Link
            to="/privacy"
            className="p-6 bg-white/80 backdrop-blur-sm rounded-2xl border-2 border-gray-200 hover:border-blue-300 hover:shadow-lg transition-all group"
          >
            <div className="flex items-start gap-4">
              <div className="p-3 rounded-xl bg-blue-100 group-hover:bg-blue-200 transition-colors">
                <Shield className="w-6 h-6 text-blue-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900 text-lg group-hover:text-blue-700 transition-colors">
                  Política de Privacidade
                </h3>
                <p className="text-sm text-gray-600 mt-1">
                  Como protegemos seus dados (LGPD)
                </p>
              </div>
              <ExternalLink className="w-5 h-5 text-gray-400 group-hover:text-blue-600 transition-colors" />
            </div>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default UserAccount;